import React, { useState, useCallback, useEffect } from 'react';
import FlexService from '../../services/FlexService';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  IconButton,
  Collapse,
  Tooltip,
  Autocomplete,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';

/**
 * AdvancedSearchPanel - Collapsible per-field search panel for workspace grids.
 *
 * Renders one input per searchable field based on entity_field_metadata.
 * Input type is determined by the field's search_type:
 * - TEXT: free text input
 * - EXACT: text input or dropdown (if allowed_values exists)
 * - RANGE: two inputs (from/to) for dates and numbers
 * - ENUM: dropdown from allowed_values or status_mapping
 */
const AdvancedSearchPanel = ({ fieldMetadata, onSearch, onClear, colors, rows }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValues, setSearchValues] = useState({});
  // choice lists owned by a script (optionsScript), loaded once per field: { fieldName: [{value, label}] }
  const [scriptOptions, setScriptOptions] = useState({});

  useEffect(() => {
    (fieldMetadata || []).filter((f) => f.optionsScript).forEach(async (f) => {
      try {
        const res = await FlexService.executeScript({ scriptName: f.optionsScript, context: {} });
        const list = res?.context?.result;
        if (res?.success && Array.isArray(list)) setScriptOptions((prev) => ({ ...prev, [f.fieldName]: list }));
      } catch (err) {
        console.error('Failed to load search options from', f.optionsScript, err);
      }
    });
  }, [fieldMetadata]);

  // Get searchable fields — smart filtering by type:
  // - TEXT/STRING: always show
  // - ENUM/STATUS: only if allowedValues or statusMapping defined
  // - BOOLEAN: skip (already handled by chip filters)
  // - NUMBER/DATE/DATETIME: show as range inputs
  const searchableFields = (fieldMetadata || []).filter((f) => {
    if (f.isSearchable === false || f.fieldName === 'id') return false;
    const fieldType = (f.fieldType || '').toUpperCase();
    const searchType = (f.searchType || 'TEXT').toUpperCase();

    // Boolean fields — already filterable via chips, skip
    if (fieldType === 'BOOLEAN' || fieldType === 'CHECKBOX') return false;

    // ENUM/STATUS fields — only show if they have defined options
    if (searchType === 'ENUM' || fieldType === 'STATUS' || fieldType === 'ENUM' || fieldType === 'DROPDOWN') {
      return !!(f.allowedValues || f.statusMapping || f.optionsScript);
    }

    // TEXT, NUMBER, DATE, DATETIME, RANGE, COMBOBOX etc. — show
    return true;
  });

  if (searchableFields.length === 0) return null;

  const handleFieldChange = (fieldName, value, rangeKey) => {
    setSearchValues((prev) => {
      if (rangeKey) {
        // Range fields store { from, to }
        const existing = prev[fieldName] || {};
        return { ...prev, [fieldName]: { ...existing, [rangeKey]: value } };
      }
      return { ...prev, [fieldName]: value };
    });
  };

  const handleSearch = () => {
    // Build params from non-empty search values
    const params = {};
    Object.entries(searchValues).forEach(([field, value]) => {
      if (value === null || value === undefined || value === '') return;
      if (typeof value === 'object' && !Array.isArray(value)) {
        // Range values
        if (value.from) params[`${field}From`] = value.from;
        if (value.to) params[`${field}To`] = value.to;
      } else {
        params[field] = value;
      }
    });
    onSearch(params);
  };

  const handleClear = () => {
    setSearchValues({});
    if (onClear) onClear();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Get allowed values for a field (from allowed_values or status_mapping)
  const getOptions = (field) => {
    if (scriptOptions[field.fieldName]) return scriptOptions[field.fieldName];
    if (field.allowedValues) {
      const vals = typeof field.allowedValues === 'string'
        ? JSON.parse(field.allowedValues)
        : field.allowedValues;
      return vals.map((v) => (typeof v === 'object' ? v : { label: v, value: v }));
    }
    if (field.statusMapping) {
      const mapping = typeof field.statusMapping === 'string'
        ? JSON.parse(field.statusMapping)
        : field.statusMapping;
      return Object.entries(mapping).map(([value, config]) => ({
        label: config.label || value,
        value,
      }));
    }
    return [];
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 0,
      bgcolor: colors.background,
      '& fieldset': { borderColor: colors.border },
      '&:hover fieldset': { borderColor: '#22c55e' },
      '&.Mui-focused fieldset': { borderColor: '#22c55e' },
    },
    '& .MuiInputBase-input': { color: colors.textPrimary, fontSize: '0.8rem' },
    '& .MuiInputLabel-root': { color: colors.textMuted, fontSize: '0.8rem' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#22c55e' },
    '& .MuiSelect-select': { color: colors.textPrimary, fontSize: '0.8rem' },
  };

  const renderField = (field) => {
    const searchType = field.searchType || 'TEXT';
    const fieldName = field.fieldName;
    const value = searchValues[fieldName];

    if (searchType === 'ENUM' || (searchType === 'EXACT' && getOptions(field).length > 0)) {
      const options = getOptions(field);
      return (
        <FormControl size="small" sx={{ minWidth: 150, ...inputSx }} key={fieldName}>
          <InputLabel>{field.displayLabel}</InputLabel>
          <Select
            value={value || ''}
            onChange={(e) => handleFieldChange(fieldName, e.target.value)}
            label={field.displayLabel}
          >
            <MenuItem value="">
              <em>Any</em>
            </MenuItem>
            {options.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    if (searchType === 'RANGE') {
      const rangeValue = (typeof value === 'object' && value) || {};
      const inputType = field.fieldType === 'NUMBER' ? 'number'
        : field.fieldType === 'DATE' ? 'date'
        : field.fieldType === 'DATETIME' ? 'datetime-local'
        : 'text';

      return (
        <Box key={fieldName} sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <TextField
            size="small"
            label={`${field.displayLabel} from`}
            type={inputType}
            value={rangeValue.from || ''}
            onChange={(e) => handleFieldChange(fieldName, e.target.value, 'from')}
            onKeyDown={handleKeyDown}
            InputLabelProps={inputType !== 'text' ? { shrink: true } : undefined}
            sx={{ minWidth: 130, ...inputSx }}
          />
          <Typography variant="caption" sx={{ color: colors.textMuted }}>–</Typography>
          <TextField
            size="small"
            label={`${field.displayLabel} to`}
            type={inputType}
            value={rangeValue.to || ''}
            onChange={(e) => handleFieldChange(fieldName, e.target.value, 'to')}
            onKeyDown={handleKeyDown}
            InputLabelProps={inputType !== 'text' ? { shrink: true } : undefined}
            sx={{ minWidth: 130, ...inputSx }}
          />
        </Box>
      );
    }

    // TEXT or EXACT (without options): offer the values already loaded for this field as a pick list
    const seen = new Set();
    (rows || []).forEach((r) => {
      const v = (r.data || r)[fieldName];
      if (v !== null && v !== undefined && v !== '' && typeof v !== 'object') seen.add(String(v));
    });
    if (seen.size > 0 && seen.size <= 50) {
      return (
        <Autocomplete
          key={fieldName}
          freeSolo
          size="small"
          options={[...seen].sort()}
          inputValue={value || ''}
          onInputChange={(e, v) => handleFieldChange(fieldName, v)}
          sx={{ minWidth: 150 }}
          renderInput={(params) => (
            <TextField {...params} label={field.displayLabel} onKeyDown={handleKeyDown} sx={inputSx} />
          )}
        />
      );
    }
    return (
      <TextField
        key={fieldName}
        size="small"
        label={field.displayLabel}
        value={value || ''}
        onChange={(e) => handleFieldChange(fieldName, e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={field.placeholder || `Search ${field.displayLabel}...`}
        sx={{ minWidth: 150, ...inputSx }}
      />
    );
  };

  const hasValues = Object.values(searchValues).some((v) => {
    if (v === null || v === undefined || v === '') return false;
    if (typeof v === 'object') return v.from || v.to;
    return true;
  });

  return (
    <>
      {/* Toggle button */}
      <Tooltip title={isOpen ? 'Hide advanced search' : 'Advanced search'}>
        <IconButton
          size="small"
          onClick={() => setIsOpen(!isOpen)}
          sx={{
            color: isOpen || hasValues ? '#22c55e' : colors.textSecondary,
            '&:hover': { color: '#22c55e' },
          }}
        >
          <TuneIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      {/* Collapsible panel */}
      <Collapse in={isOpen} sx={{ position: 'absolute', left: 0, right: 0, top: '100%', zIndex: 10 }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            px: 1.5,
            py: 1,
            bgcolor: colors.surface,
            borderBottom: `1px solid ${colors.border}`,
            alignItems: 'flex-end',
          }}
        >
          {searchableFields.map(renderField)}

          <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto' }}>
            <Button
              size="small"
              variant="contained"
              onClick={handleSearch}
              startIcon={<SearchIcon sx={{ fontSize: 16 }} />}
              sx={{
                borderRadius: 0,
                bgcolor: '#22c55e',
                color: '#fff',
                textTransform: 'none',
                fontSize: '0.75rem',
                '&:hover': { bgcolor: '#16a34a' },
              }}
            >
              Search
            </Button>
            {hasValues && (
              <Button
                size="small"
                onClick={handleClear}
                startIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                sx={{
                  borderRadius: 0,
                  color: colors.textSecondary,
                  textTransform: 'none',
                  fontSize: '0.75rem',
                  '&:hover': { color: '#ef4444' },
                }}
              >
                Clear
              </Button>
            )}
          </Box>
        </Box>
      </Collapse>
    </>
  );
};

export default AdvancedSearchPanel;
